import {
    Box, Divider, HStack, Icon, Image, Link, SimpleGrid, Text, VStack
} from '@chakra-ui/react';
import { useContext } from 'react';
import { FaPlayCircle } from 'react-icons/fa';
import { HiOutlineExternalLink } from "react-icons/hi";
import SDKIntegrationGrid from '../../components/SDKIntegrationGrid';
import { UserContext } from '../../providers';

const LINK_ICON_SRC = "https://ik.imagekit.io/ikmedia/link-icon-onboarding.svg"

export const SideNav = (): React.ReactElement => {
    const { user } = useContext(UserContext)

    if (user?.pricingPlan?.planType === "DAM_ONLY") {
        return <DAM_ONLY_SIDENAV />
    } else {
        return <DEFAULT_SIDENAV />
    }
}

const DAM_ONLY_SIDENAV = (): React.ReactElement => {
    return (
        <Box w={"500px"} bg={"appPaleGrey"} borderRadius={"8px"} padding={8}>
            <VStack divider={<Seperator />}>
                <VStack spacing={4} alignItems={"start"} w={"full"}>
                    <Heading title="Watch Imagekit Demo" />
                    <VideoThumbnail imageSrc="https://ik.imagekit.io/ikmedia/dam-only-video-thumb.webp" link={"https://www.youtube.com/watch?v=fyaisf7jYDU"} />
                </VStack>

                <VStack spacing={4} alignItems={"start"} w={"full"}>
                    <Heading title="Advanced DAM Features" />
                    <LinkList links={[
                        {
                            title: "Edit images inside the DAM",
                            link: "https://imagekit.io/docs/dam/edit-images#built-in-image-editor"
                        },
                        {
                            title: "Remove image background",
                            link: "https://imagekit.io/docs/dam/edit-images#ai-powered-background-removal"
                        },
                        {
                            title: "Upload & manage media via APIs",
                            link: "https://imagekit.io/docs/api-reference/upload-file/upload-file"
                        },
                        {
                            title: "Search assets",
                            link: "https://imagekit.io/docs/dam/search"
                        },
                        {
                            title: "Notification center",
                            link: "https://imagekit.io/docs/dam/notification-center"
                        },
                        {
                            title: "Integrate DAM with your CMS",
                            link: "https://imagekit.io/docs/dam/embeddable-media-library-widget"
                        }
                    ]} />
                </VStack>

                <VStack spacing={2} alignItems={"start"} w={"full"}>
                    <Text color={"appSlate"}>
                        Need assistance migrating assets to ImageKit DAM?
                    </Text>
                    <HStack>
                        <Icon as={HiOutlineExternalLink} color="appClearBlue" boxSize={5} />
                        <Link href="https://imagekit.io/contact-us/" target="_blank">Contact us</Link>
                    </HStack>
                </VStack>

                <VStack spacing={2} alignItems={"start"} w={"full"}>
                    <Text color={"appSlate"}>
                        Looking to embed ImageKit DAM into your web app or CMS?
                    </Text>
                    <HStack>
                        <Icon as={HiOutlineExternalLink} color="appClearBlue" boxSize={5} />
                        <Link href="https://imagekit.io/docs/dam/embeddable-media-library-widget" target="_blank">Learn how to do it</Link>
                    </HStack>
                </VStack>

            </VStack>
        </Box>
    )
}

const DEFAULT_SIDENAV = (): React.ReactElement => {
    return (
        <Box w={"500px"} bg={"appPaleGrey"} borderRadius={"8px"} padding={8}>
            <VStack divider={<Seperator />}>
                <VStack spacing={4} alignItems={"start"} w={"full"}>
                    <Heading title="Watch Imagekit Demo" />
                    <VideoThumbnail imageSrc="https://ik.imagekit.io/ikmedia/mp-video-thumb.webp" link={"https://www.youtube.com/watch?v=guD6QkrEa6Y"} />
                </VStack>

                <VStack spacing={4} alignItems={"start"} w={"full"}>
                    <Heading title="Integration" />
                    <SDKIntegrationGrid columns={4} gap={4} sdkKeys={['react', 'nextjs', 'angular', 'android', 'iOS', 'netlify', 'javascript', 'vuejs']} />
                    <LinkList links={[
                        {
                            title: "More SDKs and plugins",
                            link: "https://imagekit.io/docs/quick-start-guides"
                        }
                    ]} />
                </VStack>

                <VStack spacing={2} alignItems={"start"} w={"full"}>
                    <Heading title="Migration" />
                    <SimpleGrid columns={2} gap={4}>
                        <Link
                            _hover={{ textDecoration: 'none', outline: 'none' }}
                            color="currentcolor"
                            href="https://imagekit.io/docs/migration/cloudinary-to-imagekit"
                            isExternal
                        >
                            <VStack spacing={2}>
                                <Image src="https://ik.imagekit.io/ikmedia/cloudinary-icon.svg" w={"32px"} />
                                <Text color={"appBattleshipGrey"} fontSize={"12px"} fontWeight="400">
                                    Cloudinary
                                </Text>
                            </VStack>
                        </Link>
                        <Link
                            _hover={{ textDecoration: 'none', outline: 'none' }}
                            color="currentcolor"
                            href="https://imagekit.io/docs/migration/imgix-to-imagekit"
                            isExternal
                        >
                            <VStack spacing={2}>
                                <Image src="https://ik.imagekit.io/ikmedia/sdk-icons@3x.webp" w={"32px"} />
                                <Text color={"appBattleshipGrey"} fontSize={"12px"} fontWeight="400">
                                    Imgix
                                </Text>
                            </VStack>
                        </Link>
                    </SimpleGrid>
                </VStack>

                <VStack spacing={4} alignItems={"start"} w={"full"}>
                    <Heading title="Advanced Media Processing" />
                    <LinkList links={[
                        {
                            title: "Named Transformation or aliases",
                            link: "https://imagekit.io/docs/transformations#named-transformations"
                        },
                        {
                            title: "Securing your media URLs",
                            link: "https://imagekit.io/docs/media-delivery-basic-security"
                        },
                        {
                            title: "Text, Image and other overlays using Layers",
                            link: "https://imagekit.io/docs/transformations#overlay-using-layers"
                        },
                        {
                            title: "Smart crop",
                            link: "https://imagekit.io/docs/image-resize-and-crop#auto-smart-cropping---fo-auto"
                        },
                        {
                            title: "Adaptive Bitrate Streaming",
                            link: "https://imagekit.io/docs/adaptive-bitrate-streaming"
                        }
                    ]} />
                </VStack>

                <VStack spacing={4} alignItems={"start"} w={"full"}>
                    <Heading title="Digital Asset Management" />
                    <LinkList links={[
                        {
                            title: "Upload and Manage assets in DAM",
                            link: "https://imagekit.io/docs/dam/upload-assets"
                        },
                        {
                            title: "Invite other users",
                            link: "https://imagekit.io/docs/user-management"
                        },
                        {
                            title: "Share assets with users",
                            link: "https://imagekit.io/docs/dam/sharing-and-collaboration"
                        },
                        {
                            title: "Integrate DAM with your CMS",
                            link: "https://imagekit.io/docs/dam/embeddable-media-library-widget"
                        }
                    ]} />
                </VStack>

            </VStack>
        </Box>
    )
}

const Heading = ({ title }) => {
    return (
        <Text as="h2" fontWeight={"600"}>
            {title}
        </Text>
    )
}

const LinkList = ({ links }) => {
    if (!links.length) return null
    return (
        <VStack alignItems={"start"}>
            {links.map(({ title, link }, index) => (
                <Link href={link} target="_blank">
                    <HStack key={index} spacing={2}>
                        <Image src={LINK_ICON_SRC} alt="Link Icon" boxSize={6} color={"appClearBlue"} />
                        <Text fontWeight={500}>{title}</Text>
                    </HStack>
                </Link>
            ))}
        </VStack>
    )
}

const Seperator = () => {
    return (
        <Box w={"full"} height='50px' display={"flex"} alignItems={"center"} justifyContent={"center"}>
            <Divider orientation='horizontal' />
        </Box>
    )
}

const VideoThumbnail = ({ imageSrc, link }) => {
    return (
        <a href={link} target={"_blank"} rel={"noreferrer"}>
            <Box
                position="relative"
                borderRadius="8px"
                overflow="hidden"
            >
                {/* Image with border radius */}
                <Image
                    src={imageSrc}
                    alt="Video Thumbnail"
                    objectFit="cover"
                    width="100%"
                    height="100%"
                    borderRadius="8px"
                />

                {/* Black blanket (overlay) */}
                <Box
                    position="absolute"
                    top="0"
                    left="0"
                    width="100%"
                    height="100%"
                    backgroundColor="rgba(0, 0, 0, 0.2)"  // Semi-transparent black blanket
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="8px"  // Match border radius of the image
                >
                    {/* Play Icon centered */}
                    <Icon as={FaPlayCircle} color="white" boxSize={12} />
                </Box>
            </Box>
        </a>
    );
};